import {Component, forwardRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {Subject} from 'rxjs';
import {PlantStatisticalProfile, PodStatisticalProfile, UserType} from 'smag-commons/cfg/hierarchy';
import {DialogService} from '../dialog/dialog.service';
import {PodStatisticalProfileTemplate, RateMode, WindPlantStatisticalProfileTemplate} from 'smag-commons/cfg';
import {takeUntil} from 'rxjs/operators';
import {ConfigurationService} from '../configuration.service';
import {PvPlantStatisticalProfileTemplate} from 'smag-commons/cfg/templates';
import * as _ from 'lodash';
import {isConsumer, isPlant, isProducer, isProsumer} from '../statistical-profiles';

export interface ResetContext {
    userType?: UserType; // used for consumer/prosumer PODs
    rateMode: RateMode; // used for PODs, pv plants and wind plants
}

export interface StatisticalProfileResource {
    type: StatisticalProfileResourceType;
    name: string;
    zone?: string;
}

export interface StatisticalProfilePodResource extends StatisticalProfileResource {
    type: StatisticalProfileResourceType.POD;
    podType: StatisticalProfilePodResourceType;
    plants?: StatisticalProfilePlantResource[];
    profile?: PodStatisticalProfile;
}

export enum StatisticalProfilePodResourceType {
    CONSUMER,
    PRODUCER,
    PROSUMER
}

export interface StatisticalProfilePlantResource extends StatisticalProfileResource {
    type: StatisticalProfileResourceType.PV_PLANT | StatisticalProfileResourceType.WIND_PLANT;
    profile?: PlantStatisticalProfile;
    nominalPower?: number;
}

export enum StatisticalProfileResourceType {
    POD,
    PV_PLANT,
    WIND_PLANT
}

const DEFAULT_USER_TYPE: UserType = null;
const DEFAULT_RATE_MODE: RateMode = RateMode.SINGLE;

@Component({
    selector: 'app-statistical-profile',
    templateUrl: './statistical-profile.component.html',
    styleUrls: ['./statistical-profile.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => StatisticalProfileComponent),
            multi: true
        }
    ]
})
export class StatisticalProfileComponent implements OnInit, OnDestroy, ControlValueAccessor {
    private destroyed = new Subject<void>();

    @Input() resource: StatisticalProfileResource;
    @Input() resetValueProvider: (ctx: ResetContext) => Promise<PodStatisticalProfile | PlantStatisticalProfile>;
    @ViewChild('enterData') private enterDataDialog: TemplateRef<any>;

    form: FormGroup;
    rateModes: RateMode[] = [RateMode.SINGLE, RateMode.HOURLY];
    userTypes: UserType[] = [];
    showUserTypeSelect = false;
    statisticalProfile: PodStatisticalProfile | PlantStatisticalProfile = null;
    fn: any = null;
    podStatisticalProfileTemplates: PodStatisticalProfileTemplate[] = [];
    pvPlantStatisticalProfileTemplates: PvPlantStatisticalProfileTemplate[] = [];
    windPlantStatisticalProfileTemplates: WindPlantStatisticalProfileTemplate[] = [];

    constructor(private readonly configurationService: ConfigurationService,
                private readonly fb: FormBuilder,
                private readonly dialogService: DialogService) {
        this.form = this.fb.group({
            rateMode: new FormControl(DEFAULT_RATE_MODE, [Validators.required])
        });
    }

    ngOnInit() {
        this.configurationService.economicsConfig
            .pipe(takeUntil(this.destroyed))
            .subscribe(ecoCfg => {
                this.podStatisticalProfileTemplates = ecoCfg.pod_templates ?? [];
                this.pvPlantStatisticalProfileTemplates = ecoCfg.pv_plant_templates ?? [];
                this.windPlantStatisticalProfileTemplates = ecoCfg.wind_plant_templates ?? [];
            });

        if (this.isPlant || this.isProducer) {
            this.userTypes = [];
            this.showUserTypeSelect = false;
        } else if (this.isProsumer) {
            this.userTypes = [UserType.HOUSEHOLD, UserType.GENERIC];
            this.showUserTypeSelect = true;
        } else if (this.isConsumer) {
            this.userTypes = [UserType.HOUSEHOLD, UserType.PUBLIC_CHARGER, UserType.GENERIC];
            this.showUserTypeSelect = true;
        }
        if (this.showUserTypeSelect) {
            this.form.addControl('userType', new FormControl(DEFAULT_USER_TYPE, [Validators.required]));
        }

        this.form.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(value => {
                let reset = false;
                if (this.showUserTypeSelect && value.userType !== ((this.statisticalProfile as PodStatisticalProfile)?.user_type || null)) {
                    reset = true;
                }
                if (this.statisticalProfile && value.rateMode !== (this.statisticalProfile?.rate_mode || null)) {
                    reset = true;
                }
                if (reset) {
                    this.reset();
                }
            });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onClickEnterData() {
        this.dialogService.open<any, PodStatisticalProfile | PlantStatisticalProfile>({
            template: this.enterDataDialog,
            context: {}
        }).closed.then(profile => {
            if (profile) {
                this.statisticalProfile = profile;
                if (this.fn) {
                    this.fn(this.statisticalProfile);
                }
            }
        });
    }

    onClickClear() {
        this.clearForm();
        this.reset();
    }

    registerOnChange(fn: any): void {
        this.fn = fn;
    }

    registerOnTouched(fn: any): void {
        // empty implementation
    }

    writeValue(obj: any): void {
        this.statisticalProfile = _.cloneDeep(obj);
        this.setForm(this.statisticalProfile as PodStatisticalProfile | PlantStatisticalProfile);
    }

    get userType(): UserType {
        return this.form.controls.userType ? this.form.controls.userType.value : null;
    }

    get rateMode(): RateMode {
        return this.form.controls.rateMode.value;
    }

    get clearEnabled(): boolean {
        return !!this.statisticalProfile;
    }

    get isConsumer() {
        return isConsumer(this.resource);
    }

    get isProducer() {
        return isProducer(this.resource);
    }

    get isProsumer() {
        return isProsumer(this.resource);
    }

    get isPlant() {
        return isPlant(this.resource);
    }

    static statisticalProfileValidator(ctrl: AbstractControl): ValidationErrors | null {
        const profile = ctrl.value as PodStatisticalProfile | PlantStatisticalProfile;
        if (!profile) {
            return null;
        }

        // case pod
        const podProfile = profile as PodStatisticalProfile;
        if (profile.rate_mode === RateMode.SINGLE && (!!podProfile.absorbed_monthly_energies?.F1 || !!podProfile.injected_monthly_energies?.F1) ||
            profile.rate_mode === RateMode.HOURLY && (!!podProfile.absorbed_monthly_energies?.all || !!podProfile.injected_monthly_energies?.all)) {
            return {err: true};
        }

        // case plant
        const plantProfile = profile as PlantStatisticalProfile;
        if (profile.rate_mode === RateMode.SINGLE && !!plantProfile.produced_monthly_energies?.F1 ||
            profile.rate_mode === RateMode.HOURLY && !!plantProfile.produced_monthly_energies?.all) {
            return {err: true};
        }

        return null;
    }


    private setForm(profile: PodStatisticalProfile | PlantStatisticalProfile) {
        if (!profile) {
            return;
        }
        if (this.showUserTypeSelect) {
            this.form.controls.userType.setValue((profile as PodStatisticalProfile).user_type ?
                    (profile as PodStatisticalProfile).user_type : null,
                {emitEvent: false});
        }
        this.form.controls.rateMode.setValue(profile.rate_mode === RateMode.HOURLY ? RateMode.HOURLY : RateMode.SINGLE,
            {emitEvent: false});
        this.form.updateValueAndValidity();
    }

    private clearForm() {
        if (this.showUserTypeSelect) {
            this.form.controls.userType.setValue(DEFAULT_USER_TYPE, {emitEvent: false});
        }
        this.form.controls.rateMode.setValue(DEFAULT_RATE_MODE, {emitEvent: false});
        this.form.updateValueAndValidity();
    }

    private reset() {
        let newValueProm = Promise.resolve(null as PodStatisticalProfile | PlantStatisticalProfile);
        if (this.resetValueProvider) {
            newValueProm = this.resetValueProvider(this.mkResetContext());
        }
        newValueProm.then(newValue => {
            this.writeValue(newValue);
            if (this.fn) {
                this.fn(this.statisticalProfile);
            }
        });
    }

    private mkResetContext(): ResetContext {
        switch (this.resource.type) {
            case StatisticalProfileResourceType.POD:
                return {
                    userType: this.userType,
                    rateMode: this.rateMode
                };
            case StatisticalProfileResourceType.PV_PLANT:
            case StatisticalProfileResourceType.WIND_PLANT:
                return {rateMode: this.rateMode};
            default:
                return null;
        }
    }
}
